import {
  MessagePayload as FirebaseMessagePayload,
  Messaging,
  getToken,
  onMessage,
} from 'firebase/messaging';
import { getConfigValue } from '../functions/get-config-value/get-config-value';
import { Howl } from 'howler';

type MessagePayloadData = { [key: string]: string } | undefined;

interface MessagePayload<TData = MessagePayloadData>
  extends Omit<FirebaseMessagePayload, 'data'> {
  data: TData;
}

interface PushNotificationPayload {
  displayNotification?: (payload: {
    title: string;
    body: string;
    [key: string]: string | undefined;
  }) => void;
}

const playNotificationSound = () => {
  const sound = new Howl({
    src: [
      'https://zono-digital-qa-media.s3.ap-south-1.amazonaws.com/audio/notification.mp3',
    ], // Replace with the correct URL
    volume: 1.0,
  });
  sound.play();
};

export const usePushNotificationService = <TData = MessagePayloadData>({
  displayNotification,
}: PushNotificationPayload) => {
  const getClientToken = async (messaging: Messaging) => {
    try {
      // Check if service worker is ready
      if (!('serviceWorker' in navigator)) {
        console.error('Service workers are not supported in this browser');
        return null;
      }

      const registration = await navigator.serviceWorker.ready;

      const vapidKey = getConfigValue('FIREBASE_VAPID_KEY');

      if (!vapidKey) {
        console.error('FIREBASE_VAPID_KEY is not configured');
        return null;
      }

      const currentToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (currentToken) {
        console.log('FCM token obtained:', currentToken);
        return currentToken;
      }

      console.log(
        'No registration token available. Request permission to generate one.'
      );
      return null;
    } catch (err) {
      console.error('Error retrieving FCM token:', err);
      return null;
    }
  };

  const addMessageListener = (messaging: Messaging) => {
    if (!messaging) {
      console.error('❌ Firebase messaging instance not provided.');
      return;
    }

    try {
      onMessage(messaging, (payload: FirebaseMessagePayload) => {
        try {
          console.log('📩 push-notification-listener', payload);

          const msgPayload = payload as MessagePayload<TData>;

          // Play sound safely
          try {
            playNotificationSound();
          } catch (soundErr) {
            console.error('⚠️ Failed to play notification sound:', soundErr);
          }

          // Display notification safely
          if (payload?.notification?.title && payload?.notification?.body) {
            if (typeof displayNotification === 'function') {
              try {
                displayNotification({
                  title: payload.notification.title,
                  body: payload.notification.body,
                  ...(msgPayload.data ?? {}),
                });
              } catch (displayErr) {
                console.error('⚠️ Failed to display notification:', displayErr);
              }
            } else {
              console.warn(
                '⚠️ displayNotification is not defined or not a function.'
              );
            }
          } else {
            console.warn(
              '⚠️ Received notification payload without title or body.'
            );
          }
        } catch (cbErr) {
          console.error('❌ Error in onMessage callback:', cbErr, payload);
        }
      });
    } catch (listenerErr) {
      console.error(
        '❌ Failed to attach Firebase onMessage listener:',
        listenerErr
      );
    }
  };

  return { addMessageListener, getClientToken };
};
